/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Details/Components/ServiceEndpointRequestHistoryComponent" />
import * as React from "react";

import { IObservable, IReadyableObservableArray, ObservableValue, ReadyableObservableArray } from "VSS/Core/Observable";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Ago } from "VSSUI/Ago";
import { Card } from "VSSUI/Card";
import { Icon } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { ITableColumn, ITableRowDetails, SimpleTableCell, Table, TwoLineTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ZeroData } from "VSSUI/ZeroData";

import { StoreManager } from "DistributedTask/Flux/StoreManager";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Details/Resources";
import { IServiceEndpointRequestHistoryStoreProps, ServiceEndpointRequestHistoryStore } from "DistributedTaskUI/ServiceEndpoints/Details/Stores/ServiceEndpointRequestHistoryStore";
import { ServiceEndpointExecutionRecord } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { ServiceEntpointFeatureFlags } from "../Constants";
import { IScrollEndDetectionService, ScrollEndDetectionServiceName } from "../Services/ScrollEndDetectionService";

interface IServiceEndpointRequestHistoryComponentProps {
    endpointId: string;
    parentEvents?: IObservable<React.UIEvent<HTMLDivElement>>;
}

interface IExecutionRecord extends Partial<ServiceEndpointExecutionRecord> {
    isLoadRow?: boolean;
    renderRow?: (index: number, item: IExecutionRecord, details: ITableRowDetails<IExecutionRecord>) => JSX.Element;
}

export class ServiceEndpointRequestHistoryComponent extends VssComponent<IServiceEndpointRequestHistoryComponentProps>{

    private readonly _itemProvider: IReadyableObservableArray<IExecutionRecord | ObservableValue<IExecutionRecord | undefined>>;
    private readonly _tableColumns: ITableColumn<IExecutionRecord>[];
    private readonly _store: ServiceEndpointRequestHistoryStore;
    private readonly _scrollEndDetectionService: IScrollEndDetectionService;

    public static readonly IntialPortionOfRecordsToFetch: number = 30;

    constructor(props: IServiceEndpointRequestHistoryComponentProps, context: IVssComponentContext) {
        super(props, context);

        const storeProps: IServiceEndpointRequestHistoryStoreProps = {
            endpointId: props.endpointId,
            pageContext: context.pageContext
        };

        const storeInstanceId = "service-endpoint-request-history-" + props.endpointId;

        this._store = StoreManager.CreateStore<ServiceEndpointRequestHistoryStore, IServiceEndpointRequestHistoryStoreProps>(ServiceEndpointRequestHistoryStore, storeInstanceId, storeProps);
        this._tableColumns = this.createTableColumns();
        this._itemProvider = new ReadyableObservableArray<IExecutionRecord>();
        this._scrollEndDetectionService = context.pageContext.getService<IScrollEndDetectionService>(ScrollEndDetectionServiceName);
    }

    public componentDidMount(): void {
        this._store.addListener(ServiceEndpointRequestHistoryStore.LoadingFinished, this._onStoreDataLoaded);
        this._store.addListener(ServiceEndpointRequestHistoryStore.LoadingStarted, this._onStoreDataLoading);

        this._store.fetchData(ServiceEndpointRequestHistoryComponent.IntialPortionOfRecordsToFetch); //this should fill the whole screen

        if (this.props.parentEvents) {
            this.props.parentEvents.subscribe(this._onParentPageScroll, "scroll");
        }
    }

    public componentWillUnmount() {
        this._store.removeListener(ServiceEndpointRequestHistoryStore.LoadingStarted, this._onStoreDataLoading);
        this._store.removeListener(ServiceEndpointRequestHistoryStore.LoadingFinished, this._onStoreDataLoaded);
        this._store.reset();

        if (this.props.parentEvents) {
            this.props.parentEvents.unsubscribe(this._onParentPageScroll, "scroll");
        }
    }

    public render(): React.ReactNode {

        return (
            <div className={"service-connection-execution-history-tab page-content page-content-top flex flex-grow flex-column"}>
                <Observer records={this._itemProvider} ready={this._itemProvider.ready}>
                    {
                        (props: { records: IExecutionRecord[], ready: boolean }) => {

                            if (!props.ready) {
                                return <Spinner label={Resources.Loading} />;
                            }

                            if (props.records.length > 0) {
                                return this.executionHistoryTableComponent();
                            }

                            return this.emptyExecutionHistoryComponent();
                        }
                    }
                </Observer>
            </div>
        );
    }

    private executionHistoryTableComponent(): React.ReactNode {
        return (
            <Card className="bolt-card-no-vertical-padding flex-column history-filter-spacer" contentProps={{ contentPadding: false }}>
                <Table
                    className="request-history-table"
                    columns={this._tableColumns}
                    itemProvider={this._itemProvider}
                />
            </Card>
        );
    }

    private emptyExecutionHistoryComponent(): React.ReactNode {
        const image = getAssetLocation(this.context.pageContext, "ms.vss-distributedtask-web/dt-resources-hub/Content/no-results.png");

        return (
            <Card className="service-connection-empty-execution-history-card bolt-card-no-vertical-padding flex-column"
                contentProps={{ contentPadding: false }}>
                <ZeroData
                    className={"no-history-view"}
                    imagePath={image}
                    imageAltText={Resources.EmptyExecutionHistory}
                    primaryText={Resources.EmptyExecutionHistory}
                />
            </Card>
        );
    }

    private _onParentPageScroll = (event: React.UIEvent<HTMLDivElement>): void => {
        if (!isFeatureFlagEnabled(this.context.pageContext, ServiceEntpointFeatureFlags.EnableStreamlinedScrollEventsInServiceEndpointHistory)) {
            return;
        }

        if (this._scrollEndDetectionService.isAtTheBottom(event.currentTarget)) {
            this._store.fetchData();
        }
    }

    private _onStoreDataLoaded = (): void => {
        const records = this._store.getExecutionRecords();
        this._itemProvider.value = records;
        this._itemProvider.ready.value = true;
    }

    private _onStoreDataLoading = (): void => {
        const asyncRow = new ObservableValue<IExecutionRecord | undefined>(undefined);
        this._itemProvider.push(asyncRow);
    }

    public fetchMoreItems(top?: number) {
        if (isFeatureFlagEnabled(this.context.pageContext, ServiceEntpointFeatureFlags.EnableStreamlinedScrollEventsInServiceEndpointHistory)) {
            return;
        }

        if (!this._store.hasMore()) {
            return;
        }

        this._store.fetchData(top);
    }

    private _renderRunCell = (_: number, columnIndex: number, tableColumn: ITableColumn<IExecutionRecord>, tableItem1: IExecutionRecord): JSX.Element => {
        const tableItem = tableItem1 as ServiceEndpointExecutionRecord;
        const runLine = (<span className="flex-row scroll-hidden">
            <Tooltip overflowOnly={true} text={tableItem.data.owner.name}>
                <FPSLink className="fontSizeM font-size-m text-ellipsis bolt-table-link bolt-table-inline-link" href={tableItem.data.owner._links.web.href}>{tableItem.data.owner.name}</FPSLink>
            </Tooltip>
        </span>);
        const definitionLine = (<Tooltip overflowOnly={true} text={tableItem.data.definition.name}>
            <span className="fontSize font-size secondary-text flex-row flex-baseline text-ellipsis">
                <FPSLink className="text-ellipsis flex-row flex-baseline bolt-table-link bolt-table-inline-link" href={tableItem.data.definition._links.web.href}>{tableItem.data.definition.name}</FPSLink>
            </span>
        </Tooltip>);
        return (<TwoLineTableCell className="bolt-table-cell-content-with-inline-link no-v-padding" columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} line1={runLine} line2={definitionLine} />);
    }

    private _renderStageCell = (_: number, columnIndex: number, tableColumn: ITableColumn<IExecutionRecord>, tableItem: IExecutionRecord): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className="flex-row flex-center font-size">
                    {tableItem.data.ownerDetails}
                </div>
            </SimpleTableCell>
        );
    }

    private _renderTimeStartedCell = (_: number, columnIndex: number, tableColumn: ITableColumn<IExecutionRecord>, tableItem: IExecutionRecord): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className="flex-row flex-center font-size">
                    <Icon iconName="Calendar" className="calendar-icon" />
                    {tableItem.data.startTime && <Ago date={tableItem.data.startTime} format={AgoFormat.Compact} />}
                </div>
            </SimpleTableCell>
        );
    }

    private createTableColumns(): ITableColumn<IExecutionRecord>[] {
        const columns: ITableColumn<IExecutionRecord>[] = [];

        columns.push({
            id: "run",
            width: -18,
            name: Resources.UsageHistoryRunColumn,
            renderCell: this._renderRunCell
        });

        columns.push({
            id: "stage",
            width: -18,
            name: Resources.UsageHistoryStageColumn,
            renderCell: this._renderStageCell
        });

        columns.push({
            id: "time-started",
            width: -18,
            name: Resources.UsageHistoryDateColumn,
            renderCell: this._renderTimeStartedCell
        });

        return columns;
    }
}
